import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import { CSVLink } from 'react-csv';
import { useInView } from 'react-intersection-observer';
import { getJSONFileFromIpfs, initIPFS } from '../../../../state/ipfs';
import { handleResponseStatus, sendRequest } from '../../../../utils/api-request';
import CustomBtn from '../../../../generic/CustomBtn';
import Loader from '../../../../components/Loader/Loader';
import DownloadZone from '../../../../components/DownloadZone/DownloadZone';
import PreviewSampleZone from '../PreviewSampleZone/PreviewSampleZone';
import getConfig from '../../../../config';
import MapMini from '../../../../components/Map/components/MapMini/MapMini';

const { backendApi } = getConfig();

const SampleData = ({ step, totalData, setTotalData, setPreviewData, nextPage, prevPage, currentStage, setShowRequestModal }) => {
  const history = useHistory();
  const { nameId } = useParams();
  const translate = useIntl();

  const [currentDataProject, setCurrentDataProject] = useState({});
  const [sampleZonesApp, setSampleZonesApp] = useState([]);
  const [sampleZonesInfo, setSampleZonesInfo] = useState([]);
  const [chunkedSampleZones, setChunkedSampleZones] = useState([]);
  const [loadingStep, setLoadingStep] = useState(0);
  const [warning, setWarning] = useState([]);
  const [downloadZonesWarning, setDownloadZonesWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const origin = window?.location?.origin || '';
  const csvData = sampleZonesInfo.flatMap((zone) => (zone.sampleTrees || []).map((tree, index) => {
    const treeName = tree.treeName || (tree.id ? `${zone.sampleName}_T${tree.id}` : zone.sampleName);
    const treePhoto = tree.treePhoto ? `${origin}/ipfs-image?cid=${tree.treePhoto}` : '';
    const treeTag = tree.labelPhoto ? `${origin}/ipfs-image?cid=${tree.labelPhoto}` : '';
    return {
      'Name Zone': index === 0 ? (zone.sampleName || '') : '',
      'Name trees': treeName || '',
      'Tree status': tree.status || '',
      'Height, m': tree.height || '',
      'Circumference, cm': tree.diameter || '',
      'Tree photo': treePhoto || '',
      'Tree tag': treeTag || '',
    };
  }));

  const listRef = useRef();
  const { ref } = useInView({
    root: listRef.current,
    threshold: 0,
    onChange: (inView) => {
      if (sampleZonesInfo.length < totalData.sampleZones.length && inView) {
        (async () => {
          // eslint-disable-next-line no-use-before-define
          await loadSampleZones(chunkedSampleZones, loadingStep);
        })();
      }
    },
  });

  const chunkArray = (myArray, chunkSize) => {
    const results = [];
    while (myArray.length) {
      results.push(myArray.splice(0, chunkSize));
    }
    return results;
  };

  const loadSampleZones = async (chunkArr, index) => {
    setLoading(true);
    const ipfs = await initIPFS();

    const sZones = [...chunkArr[index]];

    const sampleZonesFromApp = await Promise.all(
      sZones.map(async (item) => {
        const { result, statusCode } = await sendRequest(`${backendApi}/api/dashboard/sample-zone?sample_zone_id=${item.id}`, 'GET', {}, true);
        let returnResult = {};

        handleResponseStatus(
          statusCode,
          () => {
            if (result && Object.keys(result).length) {
              const cid = result.cid;
              const reportStage = result?.stage_id;
              if (cid && reportStage === currentStage) {
                const historySampleTrees = [...item.sampleTrees.historySampleTrees];
                historySampleTrees.push({ stageId: currentStage, cid });
                const currentSampleTrees = cid;
                const sampleTrees = {
                  historySampleTrees,
                  currentSampleTrees,
                };
                returnResult = { ...item, sampleTrees };
                return;
              }
              returnResult = { ...item };
              return;
            }
            returnResult = { ...item };
          },
          () => {
            setLoading(false);
            setShowRequestModal(true);
            returnResult = { ...item };
          },
        );

        return returnResult;
      }),
    );
    const sampleZonesDetails = await Promise.all(
      sampleZonesFromApp.map(async (item) => {
        if (item) {
          const cid = item.sampleTrees.currentSampleTrees;
          const lastReportStage = item.sampleTrees.historySampleTrees.slice(-1)[0];
          if (cid && lastReportStage?.stageId === currentStage) {
            const treesJSON = await getJSONFileFromIpfs(ipfs, cid) || [];
            const parsedTreeJSON = treesJSON.map((tree, id) => {
              const treeName = Object.keys(tree)[0];
              const treeInfo = Object.values(tree)[0];
              return {
                id: tree.id ? tree.id : Date.now() + id,
                treeName,
                status: treeInfo.select.toLowerCase(),
                height: treeInfo.height,
                diameter: treeInfo.diametr,
                treePhoto: treeInfo.treeImage,
                labelPhoto: treeInfo.treeLabel,
              };
            });
            return { ...item, sampleTrees: parsedTreeJSON };
          }
          return { ...item, sampleTrees: [] };
        }
      }),
    );
    setSampleZonesApp((prev) => prev.concat(sampleZonesFromApp));
    setSampleZonesInfo((prev) => prev.concat(sampleZonesDetails));
    setLoadingStep((prev) => prev + 1);
    setLoading(false);
  };

  const onSubmit = async () => {
    if (sampleZonesInfo.length !== totalData.sampleZones.length) {
      // User try to confirm form, where not all sample zones were loaded
      setDownloadZonesWarning(true);
      return;
    }
    const errorZones = sampleZonesInfo.map((item) => ({ missingLength: (item?.sampleTrees || []).length === 0 }));
    const hasError = errorZones.find((item) => item.missingLength);
    if (hasError) {
      setWarning(errorZones);
      return;
    }

    setDownloadZonesWarning(false);
    setTotalData({ ...totalData, sampleZones: sampleZonesApp });
    setPreviewData({ ...totalData, sampleZones: sampleZonesInfo });
    nextPage();
  };
  useEffect(() => {
    const hasData = totalData.sampleZones?.length && totalData.stagesInfo?.find((item) => item.stage === currentStage);
    if (hasData) {
      const sampleZones = [...totalData.sampleZones];
      const chunks = chunkArray(sampleZones, 20);
      const currStageInfo = totalData.stagesInfo.find((item) => item.stage === currentStage);
      const currSubZone = totalData.subZones.find((item) => item.subId === currStageInfo?.subId);
      const targetSampleZones = totalData.sampleZones.filter((item) => item.subId === currStageInfo?.subId);
      setChunkedSampleZones(chunks);
      setCurrentDataProject({ ...totalData, subZones: [currSubZone], sampleZones: targetSampleZones });
    } else {
      history.push(`/data-upload/${nameId}`);
    }
  }, []);

  useEffect(async () => {
    if (chunkedSampleZones.length) {
      await loadSampleZones(chunkedSampleZones, loadingStep);
    }
  }, [chunkedSampleZones]);

  if (step !== 0) {
    return null;
  }
  const isHaveSampleTrees = sampleZonesInfo.find((item) => item.sampleTrees.length);
  return (
    <>
      <div className="upload-wizard__monitoring">
        <div className="upload-wizard__monitoring-info">
          <div className="upload-wizard__monitoring-area">
            <span>
              {translate.formatMessage({ id: 'sampleData.monitoredArea' })}
            </span>
            <p className="upload-wizard__monitoring-value">
              {totalData.subZones.reduce((sum, current) => sum + +current.square, 0)}
            </p>
          </div>
          <div className="upload-wizard__monitoring-area">
            <span>
              {translate.formatMessage({ id: 'previewReport.numberOfSamplePlots' })}
            </span>
            <p className="upload-wizard__monitoring-value">
              {totalData.sampleZones.length}
            </p>
          </div>
        </div>
        <MapMini state={currentDataProject} />
        <div className="upload-wizard__download-data">

          <CSVLink
            data={totalData.sampleZones.map((el) => [`samplePlot: ${el.sampleName}`, `longitude: ${el.coordinates[0]}`, `latitude: ${el.coordinates[1]}`])}
            filename="Coordinate"
            className="btn btn__download-zone upload-wizard__download-csv"
          >
            {translate.formatMessage({ id: 'sampleData.downloadSamplePlots' })}
          </CSVLink>
          <DownloadZone
            customClass=""
            showPosition="top"
            coordinate={totalData}
            fileName={`ofp_${totalData.name}_allMonitoring`}
            buttonText={translate.formatMessage({ id: 'previewReport.downloadMonitoringZone' })}
            downloadAll="subzones"
          />
          <DownloadZone
            customClass=""
            showPosition="top"
            fileName={`ofp_${totalData.name}_nftZone`}
            coordinate={totalData}
            buttonText={translate.formatMessage({ id: 'previewReport.downloadProjectArea' })}
          />
        </div>
        <CustomBtn
          label={translate.formatMessage({ id: 'button-editInfo' })}
          customClass="btn btn__edit-transparent"
          iconClass="icon-pencil-alt"
          type="button"
          handleClick={() => history.push(`/data-upload/${nameId}`)}
        />
      </div>
      {!!sampleZonesInfo.length && isHaveSampleTrees && (
        <CSVLink
          data={csvData}
          filename={`sample-zones-stage-${currentStage || 1}.csv`}
          className="btn btn__download-zone upload-wizard__download-csv sample"
          separator=";"
        >
          {translate.formatMessage({ id: 'previewSampleZone.downloadCsv' })}
        </CSVLink>
      )}
      <div className="upload-wizard__sz-list" ref={listRef}>

        {sampleZonesInfo.map((zone, id) => (
          <PreviewSampleZone
            sampleName={zone.sampleName}
            sampleRadius={zone.sampleRadius}
            coordinates={zone.coordinates}
            sampleTrees={zone.sampleTrees}
            warning={warning[id]}
            isLastElem={id === sampleZonesInfo.length - 1}
            ref={ref}
            key={`${zone.sampleName}${zone.id}`}
          />
        ))}
        {loading && <Loader customClass="lds-ring__small" />}
      </div>
      {sampleZonesInfo.length < totalData.sampleZones.length && (
        <div className={`upload-wizard__load-more ${downloadZonesWarning && 'upload-wizard__load-more-red'}`}>
          {downloadZonesWarning && (
            <span className="upload-wizard__load-more-warning">
              {translate.formatMessage({ id: 'sampleData.loadInformation' })}
            </span>
          )}
        </div>
      )}
      <div className="upload-wizard__panel">
        <CustomBtn
          label={translate.formatMessage({ id: 'button-backToList' })}
          handleClick={prevPage}
          customClass="btn__cancel"
        />
        <CustomBtn
          label={translate.formatMessage({ id: 'button-next' })}
          handleClick={onSubmit}
          type="submit"
        />
      </div>
    </>
  );
};

export default SampleData;
